import { useEffect, useRef, useState } from 'react'
import { Bot, Loader2, Plus, Send, Sparkles, User } from 'lucide-react'

import { Badge } from '@components/ui/badge'
import { Button } from '@components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/card'
import { Input } from '@components/ui/input'
import { aiChatApi } from '@services/ai-chat.service'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
}

export function AIChatPage() {
  const [draft, setDraft] = useState('')
  const [messages, setMessages] = useState<Message[]>([])
  const [conversationId, setConversationId] = useState<string>()
  const [configured, setConfigured] = useState<boolean>()
  const [statusError, setStatusError] = useState(false)
  const [sendError, setSendError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const mounted = useRef(true)
  const scrollRef = useRef<HTMLDivElement>(null)
  const sendController = useRef<AbortController>()
  const statusController = useRef<AbortController>()

  const loadStatus = () => {
    statusController.current?.abort()
    const controller = new AbortController()
    statusController.current = controller
    setStatusError(false)
    aiChatApi.getStatus(controller.signal).then(
      (status) => {
        if (mounted.current && !controller.signal.aborted && statusController.current === controller) setConfigured(status.configured)
      },
      () => {
        if (mounted.current && !controller.signal.aborted && statusController.current === controller) setStatusError(true)
      },
    )
  }

  useEffect(() => {
    mounted.current = true
    loadStatus()
    return () => {
      mounted.current = false
      statusController.current?.abort()
      sendController.current?.abort()
    }
  }, [])

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [messages])

  const sendMessage = async () => {
    const text = draft.trim()
    if (!text || isLoading || !configured) return
    const userMessage = { id: crypto.randomUUID(), role: 'user' as const, content: text }
    setMessages((current) => [...current, userMessage])
    setDraft('')
    setSendError(false)
    setIsLoading(true)
    const controller = new AbortController()
    sendController.current = controller
    try {
      const response = await aiChatApi.sendMessage(text, conversationId, controller.signal)
      if (!mounted.current || controller.signal.aborted) return
      setConversationId(response.conversation_id)
      setMessages((current) => [...current, { id: crypto.randomUUID(), role: 'assistant', content: response.reply }])
    } catch {
      if (!mounted.current || controller.signal.aborted) return
      setMessages((current) => current.filter((item) => item.id !== userMessage.id))
      setDraft(text)
      setSendError(true)
    } finally {
      if (mounted.current && !controller.signal.aborted) setIsLoading(false)
    }
  }

  const newConversation = () => {
    sendController.current?.abort()
    setConversationId(undefined)
    setMessages([])
    setDraft('')
    setSendError(false)
    setIsLoading(false)
  }

  return (
    <div className="container mx-auto p-6 h-[calc(100vh-4rem)]">
      <div className="max-w-4xl mx-auto h-full">
        <Card className="h-full flex flex-col">
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-2">
                <Bot className="h-6 w-6" />
                <div>
                  <CardTitle className="flex flex-wrap items-center gap-2">Asistente Scarlett AI <Badge variant="secondary"><Sparkles className="h-3 w-3 mr-1" />Beta</Badge></CardTitle>
                  <p className="text-sm text-muted-foreground">Scarlett AI apoya la redacción y explicación; no ejecuta acciones ni sustituye el criterio clínico.</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2"><Button variant="outline" onClick={newConversation} disabled={isLoading}><Plus className="h-4 w-4" />Nueva conversación</Button>
              {isLoading && <Badge variant="outline"><Loader2 className="h-3 w-3 mr-1 animate-spin" />Procesando</Badge>}</div>
            </div>
          </CardHeader>
          <CardContent className="flex min-h-0 flex-1 flex-col gap-4">
            {configured === false && <div role="status" className="rounded-md border p-3">El asistente no está configurado para esta clínica.</div>}
            {statusError && <div role="alert" className="rounded-md border p-3">No pudimos consultar el estado del asistente. <Button variant="link" onClick={loadStatus}>Reintentar</Button></div>}
            {sendError && <div role="alert" className="rounded-md border p-3">No se pudo enviar el mensaje. Tu borrador fue restaurado.</div>}
            <div className="min-h-0 flex-1 rounded-lg border bg-muted/50 p-4 overflow-y-auto" ref={scrollRef}>
              <div className="space-y-4">
                {messages.length === 0 && <div className="text-center py-12 text-muted-foreground"><Bot className="h-12 w-12 mx-auto mb-4 opacity-50" /><p className="text-lg font-medium">Bienvenido a Scarlett AI</p></div>}
                {messages.map((item) => <div key={item.id} className={`flex gap-3 ${item.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-secondary">{item.role === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}</div>
                  <div className="max-w-[80%] break-words whitespace-pre-wrap rounded-lg border bg-background p-3 text-sm">{item.content}</div>
                </div>)}
              </div>
            </div>
            <div className="flex gap-2">
              <Input value={draft} onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); void sendMessage() } }} maxLength={4000} placeholder="Escribe tu mensaje" disabled={configured !== true || isLoading} />
              <Button aria-label="Enviar" onClick={() => void sendMessage()} disabled={configured !== true || isLoading || !draft.trim()}><Send className="h-4 w-4" /></Button>
            </div>
            <p className="text-xs text-muted-foreground text-center">Verifica las respuestas antes de usarlas en la atención clínica.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
